import { useState } from 'react';
import { theme } from '../../theme.js';
import { useAuth } from '../../context/AuthContext.js';
import { useAuditLog } from '../../lib/queries.js';
import { actorLabel } from '../../lib/people.js';
import type { AuditEntry, AuditGroup } from '@keetcheck/shared';

const FILTERS: { key: AuditGroup | undefined; label: string }[] = [
  { key: undefined,     label: 'All'         },
  { key: 'people',      label: 'People'      },
  { key: 'setup',       label: 'Setup'       },
  { key: 'inspections', label: 'Inspections' },
];

const ENTITY_LABEL: Record<string, string> = {
  org: 'org settings', asset: 'asset', equipment: 'equipment', category: 'category', location: 'location',
  team: 'team', schedule: 'schedule', check_run: 'check run', check_item: 'check item',
};

export function ActivityPage() {
  const { user } = useAuth();
  const [group, setGroup] = useState<AuditGroup | undefined>(undefined);
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useAuditLog(user?.orgId ?? '', group);
  const entries = data?.pages.flat() ?? [];

  return (
    <div style={{ background: theme.bg, flex: 1 }}>
      {/* filter chips */}
      <div style={{ display: 'flex', gap: 6, padding: '14px 18px 10px', flexWrap: 'wrap' }}>
        {FILTERS.map((f) => {
          const active = f.key === group;
          return (
            <button
              key={f.label}
              onClick={() => setGroup(f.key)}
              style={{
                padding: '6px 12px', borderRadius: 999, cursor: 'pointer', fontFamily: theme.font,
                fontSize: 12, fontWeight: 600,
                border: `1px solid ${active ? theme.accent : theme.border}`,
                background: active ? theme.accent : theme.surface,
                color: active ? theme.accentInk : theme.textDim,
              }}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {isLoading && <Empty>Loading…</Empty>}
      {!isLoading && entries.length === 0 && <Empty>No activity yet.</Empty>}

      <div style={{ background: theme.surface, borderTop: `1px solid ${theme.border}` }}>
        {entries.map((e) => <EntryRow key={e.id} entry={e} />)}
      </div>

      {hasNextPage && (
        <div style={{ padding: 16, textAlign: 'center' }}>
          <button
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            style={{
              padding: '9px 18px', borderRadius: 10, cursor: 'pointer', fontFamily: theme.font,
              border: `1px solid ${theme.border}`, background: theme.surface,
              color: theme.text, fontSize: 13, fontWeight: 600,
            }}
          >
            {isFetchingNextPage ? 'Loading…' : 'Load older'}
          </button>
        </div>
      )}
    </div>
  );
}

function EntryRow({ entry }: { entry: AuditEntry }) {
  const details = changedFields(entry);
  return (
    <div style={{ padding: '11px 18px', borderBottom: `1px solid ${theme.border}` }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
        <div style={{ flex: 1, fontSize: 14, color: theme.text, lineHeight: 1.4 }}>{describe(entry)}</div>
        <time
          dateTime={entry.createdAt}
          title={new Date(entry.createdAt).toLocaleString()}
          style={{ fontSize: 11, color: theme.textMuted, whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          {fmtWhen(entry.createdAt)}
        </time>
      </div>
      {details.length > 0 && (
        <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {details.map(([field, from, to]) => (
            <div key={field} style={{ fontSize: 12, color: theme.textDim }}>
              <span style={{ color: theme.textMuted }}>{humanize(field)}:</span>{' '}
              {fmtValue(from)} → <strong style={{ fontWeight: 600 }}>{fmtValue(to)}</strong>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Rendering helpers ─────────────────────────────────────────

function describe(e: AuditEntry): string {
  const actor = e.actorName ? actorLabel(e.actorName, e.actorActive) : 'System';
  const subject = actorLabel(e.subjectName, e.subjectActive);
  const n = { ...(e.oldValue ?? {}), ...(e.newValue ?? {}) } as Record<string, unknown>;
  const role = (r: unknown) => String(r ?? '').replace('_', ' ');
  const [type, verb] = e.action.split('.') as [string, string];
  const thing = `${ENTITY_LABEL[type] ?? type}${n['name'] ? ` “${n['name']}”` : ''}`;

  switch (e.action) {
    case 'member.join':         return `${subject} joined as ${role(n['role'])}`;
    case 'member.rejoin':       return `${subject} rejoined as ${role(n['role'])}`;
    case 'member.role_change':  return `${actor} changed ${subject}’s role`;
    case 'member.rename':       return `${actor} renamed ${e.oldValue?.['displayName']} to ${e.newValue?.['displayName']}`;
    case 'member.remove':       return `${actor} removed ${subject} from the org`;
    case 'invite.create':       return `${actor} invited ${n['name']} (${n['email']}) as ${role(n['role'])}`;
    case 'invite.revoke':       return `${actor} revoked the invite for ${n['name']} (${n['email']})`;
    case 'team.member_set':     return `${actor} added ${subject} to team “${n['team']}” as ${n['role']}`;
    case 'team.member_remove':  return `${actor} removed ${subject} from team “${n['team']}”`;
    case 'team.disable':        return `${actor} disabled team “${n['name']}”`;
    case 'team.enable':         return `${actor} re-enabled team “${n['name']}”`;
    case 'schedule.rotation':   return `${actor} changed the rotation of “${n['name']}” (${n['slots']} slots, ${n['teams']} teams)`;
    case 'team.member_move':    return `${actor} moved ${subject} from team “${e.oldValue?.['team']}” to “${e.newValue?.['team']}”`;
    case 'user.rename':         return `${actor} changed their name`;
    case 'user.delete_self':    return `${actor} deleted their account`;
    case 'user.mfa_reset':      return `${actor} reset ${subject}’s two-factor authentication`;
    case 'member.reset_sign_in': return `${actor} reset ${subject}’s sign-in`;
    case 'equipment.move': {
      const qty = Number(e.newValue?.['quantity'] ?? 1);
      return `${actor} moved ${qty > 1 ? `${qty} × ` : ''}“${n['name']}” from ${e.oldValue?.['place']} to ${e.newValue?.['place']}`;
    }
    case 'equipment.retire': {
      const removed = [
        n['removed_from_layouts'] ? `${n['removed_from_layouts']} layout(s)` : null,
        n['removed_from_locations'] ? `${n['removed_from_locations']} location(s)` : null,
        n['removed_from_open_checks'] ? `${n['removed_from_open_checks']} open check(s)` : null,
      ].filter(Boolean);
      return `${actor} retired equipment “${n['name']}”${removed.length ? ` — removed from ${removed.join(', ')}` : ''}`;
    }
    case 'schedule.generate':   return `${actor} generated ${n['runs']} check run(s) for “${n['name']}” (${n['period_start']})`;
    case 'run.create':          return `${actor} started a check of “${n['asset']}” for team “${n['team']}”`;
    case 'run.submit':          return `${actor} submitted a check run`;
    case 'location.stock_set':  return `${actor} stored ${n['equipment']} (× ${n['quantity']}) at “${n['name']}”`;
    case 'location.stock_remove': return `${actor} removed ${n['equipment']} from “${n['name']}”`;
    case 'check_item.update':   return `${actor} updated a check item`;
  }
  switch (verb) {
    case 'create': return `${actor} created ${thing}`;
    case 'update': return `${actor} updated ${thing}`;
    case 'retire': return `${actor} retired ${thing}`;
  }
  return `${actor}: ${e.action}`;
}

/** [field, from, to] for every field present in both old and new values. */
function changedFields(e: AuditEntry): [string, unknown, unknown][] {
  if (!e.oldValue || !e.newValue) return [];
  return Object.keys(e.newValue)
    .filter((k) => k in e.oldValue! && JSON.stringify(e.oldValue![k]) !== JSON.stringify(e.newValue![k]))
    .map((k) => [k, e.oldValue![k], e.newValue![k]]);
}

function humanize(field: string): string {
  const s = field.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function fmtValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  const s = typeof v === 'string' ? v.replace('_', ' ') : JSON.stringify(v);
  return s.length > 60 ? `${s.slice(0, 57)}…` : s;
}

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  const mins = Math.round((Date.now() - d.getTime()) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 24 * 60) return `${Math.round(mins / 60)}h ago`;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div style={{ padding: '24px 18px', fontSize: 13, color: theme.textMuted }}>{children}</div>;
}
